import React, { useState, useEffect } from 'react'
import {
    DesktopComputerIcon,
    DocumentTextIcon,
    LockClosedIcon,
    ShieldExclamationIcon,
} from '@heroicons/react/outline'
import Button from '../../components/Button/Button'
import ContentLayout from '../../components/ContentLayout/ContentLayout'
import { faqsItems } from '../../helpers/faqsUtils'
import FAQ from './components/FAQ'
import QuickLink from './components/QuickLink'
import Download from '../../svg/Download.svg'
import CheckCircle from '../../svg/CheckCircle.svg'
import AppRoutes from '../../routes/appRoutes'
import { installPWA, promptHandler } from '../../utils/installPwa'
import { isPwaInstalled } from '../../utils/isPwaInstalled'
import StatusBadge from '../../components/StatusBadge/StatusBadge'
import useScreen from '../../hooks/useScreen'
import useAuth from '../../hooks/useAuth'
import InstructionsForIOSModal from './components/InstructionsForIOSModal'
import InstructionsForChromeModal from './components/InstructionsForChromeModal'
import InstructionsForNotChromeModal from './components/InstructionsForNotChromeModal'
import IOSIcon from '../../svg/IOSIcon.svg'
import useDeviceDetection from '../../hooks/useDeviceDetection'

const SupportPage = () => {
    const pwaIsInstalled = isPwaInstalled()
    const { isMobile, isTablet } = useScreen()
    const {
        isMobileiOS,
        isIPad,
        isDesktopSafari,
        isChrome,
        isEdge,
    } = useDeviceDetection()
    const { user } = useAuth()
    const isDesktop = !isMobile && !isTablet
    const [modalIOSOpen, setModalIOSOpen] = useState(false)
    const [modalChromeOpen, setModalChromeOpen] = useState(false)
    const [modalNotChromeOpen, setModalNotChromeOpen] = useState(false)
    const [isPromptAvailable, setIsPromptAvailable] = useState(false)

    const [faqs, setFaqs] = useState(
        faqsItems?.map((item, index) => {
            return {
                ...item,
                id: index,
                active: false,
            }
        }),
    )

    const changeFaq = id => {
        const newFaqs = faqs?.map(item => {
            return {
                ...item,
                active: item?.active ? false : item?.id === id,
            }
        })
        setFaqs(newFaqs)
    }

    const NewFeatureComponent = () => {
        return (
            <div className="mt-auto">
                <StatusBadge displayClass="New Feature" />
            </div>
        )
    }

    useEffect(() => {
        let timeoutId

        const checkPrompt = async () => {
            const isAvailable = await promptHandler()
            setIsPromptAvailable(isAvailable)

            if (!isAvailable) {
                // Retry one time after 2 seconds
                timeoutId = setTimeout(async () => {
                    const retryAvailable = await promptHandler()
                    setIsPromptAvailable(retryAvailable)
                }, 2000)
            }
        }

        checkPrompt()

        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId)
            }
        }
    }, [])

    return (
        <ContentLayout>
            <InstructionsForIOSModal
                isOpen={modalIOSOpen}
                setIsOpen={setModalIOSOpen}
                isMobile={isMobileiOS}
                isIPad={isIPad}
            />
            <InstructionsForChromeModal
                isOpen={modalChromeOpen}
                setIsOpen={setModalChromeOpen}
                isDesktop={isDesktop}
                isChrome={isChrome}
            />
            <InstructionsForNotChromeModal
                isOpen={modalNotChromeOpen}
                setIsOpen={setModalNotChromeOpen}
                isDesktop={isDesktop}
            />
            {/* Image section */}
            <div className="w-full h-40 min-h-40 relative bg-support-image bg-fixed flex flex-col items-center justify-center gap-y-2">
                <p className="text-yellow text-4xl font-medium">
                    Need assistance?
                </p>
                <p className="text-white font-medium text-lg">
                    We&apos;re here to help
                </p>
            </div>

            {/* Installation section */}
            <div className="w-full h-auto flex flex-col lg:flex-row items-center justify-start">
                <div className="w-full lg:w-1/2 h-full bg-white py-12 px-4 lg:py-20 lg:px-16 flex flex-col items-center justify-center gap-y-8">
                    <div className="w-full h-auto flex flex-col items-start justify-start gap-y-4">
                        <div className="w-full flex flex-col lg:flex-row items-start">
                            {!pwaIsInstalled && !isDesktop && (
                                <div className="lg:w2/5 flex flex-col justify-between mb-3">
                                    <NewFeatureComponent />
                                </div>
                            )}

                            <div className="w-full lg:w-3/5 flex flex-col items-start">
                                {pwaIsInstalled ? (
                                    <p className="text-black90 font-medium text-2.5xl leading-8">
                                        You&apos;re all set!
                                    </p>
                                ) : (
                                    <p className="text-black90 font-medium text-2.5xl leading-8">
                                        Save LiftOne Now
                                        {isDesktop ? <br /> : ' '}
                                        to your homescreen
                                    </p>
                                )}
                            </div>

                            {!pwaIsInstalled && isDesktop && (
                                <div className="lg:w2/5 flex flex-col justify-between mt-7 lg:mb-0">
                                    <NewFeatureComponent />
                                </div>
                            )}
                        </div>

                        {pwaIsInstalled ? (
                            <p className="text-textDarkGray font-normal leading-5 text-base">
                                LiftOne Now is now installed on your device for
                                quick and easy access.
                            </p>
                        ) : (
                            <p className="text-textDarkGray font-normal leading-5 text-base">
                                Follow the instructions below and pin LiftOne
                                Now {isDesktop && <br />}
                                to your homescreen, so you can quickly access it{' '}
                                {isDesktop && <br />}
                                with a single tap.
                            </p>
                        )}
                    </div>
                    <div className="w-full h-auto flex flex-col lg:flex-row items-center lg:items-start justify-center lg:justify-start gap-y-4 gap-x-4">
                        {!pwaIsInstalled ? (
                            isPromptAvailable ? (
                                <Button
                                    variant="primary"
                                    text="Download"
                                    className="flex-grow lg:flex-grow-0 w-full lg:w-32 h-12 lg:min-w-56 flex items-center justify-center"
                                    onClick={() => installPWA(user)}
                                    icon={
                                        <img
                                            alt="Download"
                                            src={Download}
                                            className="w-6 h-6 mr-2"
                                        />
                                    }
                                />
                            ) : isMobileiOS || isDesktopSafari ? (
                                <Button
                                    variant="primary"
                                    text="Instructions to download"
                                    className="flex-grow lg:flex-grow-0 w-full lg:w-60 h-12 lg:min-w-56"
                                    onClick={() =>
                                        setModalIOSOpen(!modalIOSOpen)
                                    }
                                    icon={
                                        <img
                                            alt="IOS Icon"
                                            src={IOSIcon}
                                            className="w-6 h-6"
                                        />
                                    }
                                />
                            ) : isChrome || isEdge ? (
                                <Button
                                    variant="primary"
                                    text="Instructions to download"
                                    className="flex-grow lg:flex-grow-0 w-full lg:w-60 h-12 lg:min-w-56"
                                    onClick={() =>
                                        setModalChromeOpen(!modalChromeOpen)
                                    }
                                />
                            ) : (
                                <Button
                                    variant="primary"
                                    text="Instructions to download"
                                    className="flex-grow lg:flex-grow-0 w-full lg:w-60 h-12 lg:min-w-56"
                                    onClick={() =>
                                        setModalNotChromeOpen(
                                            !modalNotChromeOpen,
                                        )
                                    }
                                />
                            )
                        ) : (
                            <Button
                                variant="primary"
                                text="Already downloaded"
                                className="flex-grow lg:flex-grow-0 w-full lg:w-52 h-12 lg:min-w-48 flex items-center justify-center text-textDarkerGray bg-pageBackground"
                                disabled={true}
                                icon={
                                    <img
                                        alt="Already downloaded"
                                        src={CheckCircle}
                                        className="w-6 h-6 mr-2"
                                    />
                                }
                            />
                        )}
                    </div>
                </div>
                {/* Out Location Section*/}
                <div className="w-full lg:w-1/2 h-full bg-black80 py-12 px-4 lg:py-20 lg:px-16 flex flex-col items-start justify-center gap-y-4">
                    <p className="font-medium text-3xl leading-8 text-white">
                        Our Locations
                    </p>
                    <p className="text-black30 font-normal text-base">
                        Whether your business needs its fleet serviced, new
                        equipment or a rental to support your operations, we
                        have a location specialized to cover any of your
                        warehouse needs. Click on the link below and find your
                        nearest location.
                    </p>
                    <a
                        href="https://www.liftone.net/locations/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full h-auto"
                    >
                        <Button
                            text="View Locations"
                            variant="tertiary"
                            className="h-12 w-full flex-grow lg:flex-grow-0 lg:w-36"
                        />
                    </a>
                </div>
            </div>

            {/* FAQS */}
            <div className="w-full h-auto py-12 px-4 lg:py-20 lg:px-16 bg-pageBackground flex flex-col lg:flex-row items-start justify-start gap-y-10 lg:gap-x-20">
                <div className="flex flex-col items-start justify-start gap-y-4 w-auto">
                    <h2 className="text-black90 font-medium text-2.5xl w-full max-w-full break-words whitespace-normal overflow-hidden">
                        Frequently Asked Questions
                    </h2>
                    <div className="flex flex-row lg:flex-col gap-x-1 items-start justify-start gap-y-1">
                        <p className="text-black30 font-normal text-base leading-4.68">
                            Can&apos;t find the answer you need?
                        </p>
                        <p
                            onClick={() =>
                                (window.location = 'mailto:help@liftonenow.com')
                            }
                            className="text-links cursor-pointer font-normal text-base hover:underline leading-4.68"
                        >
                            Contact Us
                        </p>
                    </div>
                </div>
                {/* FAQs Items */}
                <div className="w-full h-auto flex items-center justify-start flex-col gap-y-4">
                    {faqs?.map(item => {
                        return (
                            <FAQ
                                key={`faq-${item?.id}`}
                                title={item?.title}
                                description={item?.description}
                                setFaq={changeFaq}
                                activeFaq={faqs?.find(
                                    item => item?.active === true,
                                )}
                                id={item?.id}
                            />
                        )
                    })}
                </div>
            </div>
            {/* Quick Links Sections */}
            <div className="py-20 px-0 flex flex-col items-center justify-start gap-y-8 bg-white w-full">
                <p className="text-black90 font-medium text-3xl">Quick Links</p>
                <div className="flex lg:flex-row flex-col items-center justify-center gap-x-10 gap-y-10">
                    <QuickLink
                        title="Privacy Policy"
                        description=""
                        link="Read here"
                        href={AppRoutes.privacyPolicy()}
                        icon={
                            <ShieldExclamationIcon className="text-black30 w-6 h-6" />
                        }
                        newTab
                    />
                    <QuickLink
                        title="Terms & Conditions"
                        description=""
                        link="Read them here"
                        href={AppRoutes.termsAndConditions()}
                        icon={
                            <DocumentTextIcon className="text-black30 w-6 h-6" />
                        }
                        newTab
                    />
                    <QuickLink
                        title="Reset Password"
                        description="Forgot Password?"
                        link="Reset it here"
                        href={AppRoutes.profilePassword()}
                        icon={
                            <LockClosedIcon className="text-black30 w-6 h-6" />
                        }
                    />
                    <QuickLink
                        title="Supported Browsers"
                        link="View List"
                        icon={
                            <DesktopComputerIcon className="text-black30 w-6 h-6" />
                        }
                        href={AppRoutes.supportedBrowsers()}
                        newTab
                    />
                </div>
            </div>

            <div className="w-full h-auto bg-black90 py-12 px-4 lg:py-20 lg:px-16 gap-12 lg:gap-20 flex flex-col lg:flex-row items-start justify-start">
                <p className="text-white font-medium text-3xl leading-8 min-w-max">
                    Get in Touch
                </p>

                <div className="w-full flex-grow flex flex-col items-start justify-start gap-y-8">
                    <div className="w-full flex flex-col lg:flex-row items-start justify-start h-auto lg:h-18 gap-y-8">
                        <div className="w-full lg:w-1/2 flex flex-col items-start justify-start gap-y-2.5">
                            <p className="text-white font-medium text-lg leading-5.25">
                                General Information
                            </p>
                            <p className="w-full max-w-full truncate text-white font-normal text-base leading-5">
                                Toll Free:{' '}
                                <span className="text-black30">
                                    855-LiftOne or{' '}
                                    <a
                                        className="text-yellow hover:underline"
                                        href="tel:8555438663"
                                    >
                                        855-543-8663
                                    </a>
                                </span>
                            </p>
                        </div>
                        <div className="w-full lg:w-1/2 flex flex-col items-start justify-start gap-y-2.5">
                            <p className="text-white font-medium text-lg leading-5.25">
                                Customer Support
                            </p>
                            <div className="w-full flex flex-col items-start justify-start gap-y-1">
                                <p className="max-w-full truncate text-white font-normal text-base leading-5 ">
                                    Email:{' '}
                                    <span
                                        className="text-yellow hover:underline font-normal cursor-pointer"
                                        onClick={() =>
                                            (window.location =
                                                'mailto:help@liftonenow.com')
                                        }
                                    >
                                        help@liftonenow.com
                                    </span>
                                </p>
                                <em className="text-black30 text-sm font-normal">
                                    Including questions related to your LiftOne
                                    Now account
                                </em>
                            </div>
                        </div>
                    </div>

                    <div className="w-full flex flex-col lg:flex-row items-start justify-start h-auto lg:h-18 gap-y-8">
                        <div className="w-full lg:w-1/2 flex flex-col items-start justify-start gap-y-2.5">
                            <p className="text-white font-medium text-lg leading-5.25">
                                Paperless Invoices
                            </p>
                            <p className="w-full max-w-full truncate text-yellow font-normal text-base leading-5">
                                <a
                                    href="https://secure.billtrust.com/liftone/ig/signin"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-yellow hover:underline"
                                >
                                    Log In
                                </a>

                                <span className="text-black30 no-underline">
                                    {' '}
                                    or{' '}
                                </span>
                                <a
                                    href="https://secure.billtrust.com/liftone/ig/signup"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-yellow hover:underline"
                                >
                                    Sign Up
                                </a>
                            </p>
                        </div>
                        <div className="w-full lg:w-1/2 flex flex-col items-start justify-start gap-y-2.5">
                            <p className="text-white font-medium text-lg leading-5.25">
                                Telemetry Support
                            </p>
                            <p className="max-w-full truncate text-white font-normal text-base leading-5 ">
                                Email:{' '}
                                <span
                                    className="text-yellow hover:underline cursor-pointer"
                                    onClick={() =>
                                        (window.location =
                                            'mailto:telemetrysupport@liftone.net')
                                    }
                                >
                                    telemetrysupport@liftone.net
                                </span>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </ContentLayout>
    )
}

export default SupportPage
